import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import type { CalendarEvent, CreateCalendarEventInput, UpdateCalendarEventInput, Task } from '@frappi/shared';
import { api } from '@/lib/api';

export const calendarKeys = {
  all: ['calendar'] as const,
  events: () => [...calendarKeys.all, 'events'] as const,
  eventList: (filters?: { startDate?: string; endDate?: string; studentId?: string }) =>
    [...calendarKeys.events(), filters] as const,
  event: (id: string) => [...calendarKeys.events(), id] as const,
  day: (date: string) => [...calendarKeys.all, 'day', date] as const,
  week: (date: string) => [...calendarKeys.all, 'week', date] as const,
  month: (date: string) => [...calendarKeys.all, 'month', date] as const,
};

export interface CalendarDayData {
  date: string;
  events: CalendarEvent[];
  tasks: Task[];
}

export interface CalendarWeekData {
  startDate: string;
  endDate: string;
  events: CalendarEvent[];
  tasks: Task[];
}

export interface CalendarMonthData {
  startDate: string;
  endDate: string;
  events: CalendarEvent[];
  tasks: Task[];
}

export function useCalendarEvents(filters?: {
  startDate?: string;
  endDate?: string;
  studentId?: string;
}) {
  return useQuery({
    queryKey: calendarKeys.eventList(filters),
    queryFn: () => api.calendar.listEvents(filters),
  });
}

export function useCalendarEvent(id: string) {
  return useQuery({
    queryKey: calendarKeys.event(id),
    queryFn: () => api.calendar.getEvent(id),
    enabled: !!id,
  });
}

export function useCalendarDay(date: string) {
  return useQuery({
    queryKey: calendarKeys.day(date),
    queryFn: () => api.calendar.getDay(date),
    enabled: !!date,
  });
}

export function useCalendarWeek(date: string) {
  return useQuery({
    queryKey: calendarKeys.week(date),
    queryFn: () => api.calendar.getWeek(date),
    enabled: !!date,
  });
}

export function useCalendarMonth(date: string) {
  return useQuery({
    queryKey: calendarKeys.month(date),
    queryFn: () => api.calendar.getMonth(date),
    enabled: !!date,
  });
}

export function useCreateCalendarEvent() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('calendar');

  return useMutation({
    mutationFn: (data: CreateCalendarEventInput) => api.calendar.createEvent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
      toast.success(t('messages.created'));
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useUpdateCalendarEvent() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('calendar');

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCalendarEventInput }) =>
      api.calendar.updateEvent(id, data),
    onSuccess: (event) => {
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
      queryClient.setQueryData(calendarKeys.event(event.id), event);
      toast.success(t('messages.updated'));
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useDeleteCalendarEvent() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('calendar');

  return useMutation({
    mutationFn: (id: string) => api.calendar.deleteEvent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
      toast.success(t('messages.deleted'));
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}
